import type { BusinessProfile } from '@/types'

export type RegionCode = 'ZA' | 'GB' | 'US'

export interface LocalisationConfig {
  regionCode: RegionCode
  locale: string
  currency: string
  currencySymbol: string
  currencySymbolPosition: 'prefix' | 'suffix'
  decimalSeparator: ',' | '.'
  thousandSeparator: ' ' | ',' | '.'
  taxLabel: string
  defaultTaxRate: number
  quoteLabel: string
  invoiceLabel: string
  phoneCountryCode: string
  measurementSystem: 'metric' | 'imperial'
}

const REGION_DEFAULTS: Record<RegionCode, LocalisationConfig> = {
  ZA: {
    regionCode: 'ZA',
    locale: 'en-ZA',
    currency: 'ZAR',
    currencySymbol: 'R',
    currencySymbolPosition: 'prefix',
    decimalSeparator: ',',
    thousandSeparator: ' ',
    taxLabel: 'VAT',
    defaultTaxRate: 0.15,
    quoteLabel: 'Quote',
    invoiceLabel: 'Invoice',
    phoneCountryCode: '+27',
    measurementSystem: 'metric',
  },
  GB: {
    regionCode: 'GB',
    locale: 'en-GB',
    currency: 'GBP',
    currencySymbol: '£',
    currencySymbolPosition: 'prefix',
    decimalSeparator: '.',
    thousandSeparator: ',',
    taxLabel: 'VAT',
    defaultTaxRate: 0.20,
    quoteLabel: 'Quote',
    invoiceLabel: 'Invoice',
    phoneCountryCode: '+44',
    measurementSystem: 'metric',
  },
  US: {
    regionCode: 'US',
    locale: 'en-US',
    currency: 'USD',
    currencySymbol: '$',
    currencySymbolPosition: 'prefix',
    decimalSeparator: '.',
    thousandSeparator: ',',
    taxLabel: 'Tax',
    defaultTaxRate: 0,
    quoteLabel: 'Estimate',
    invoiceLabel: 'Invoice',
    phoneCountryCode: '+1',
    measurementSystem: 'imperial',
  },
}

export const DEFAULT_LOCALISATION_CONFIG = REGION_DEFAULTS.ZA

function sanitizeLocale(value: string | null | undefined) {
  const candidate = String(value ?? '').trim()
  return candidate || null
}

function toRegionCode(value: string | null | undefined): RegionCode | null {
  const normalized = String(value ?? '').trim().toUpperCase()
  if (normalized === 'ZA' || normalized === 'GB' || normalized === 'US') return normalized
  return null
}

function countryToRegionCode(value: string | null | undefined): RegionCode {
  const normalized = String(value ?? '').trim().toUpperCase()
  if (normalized === 'SOUTH AFRICA' || normalized === 'ZA') return 'ZA'
  if (normalized === 'UNITED KINGDOM' || normalized === 'GB' || normalized === 'UK') return 'GB'
  if (normalized === 'UNITED STATES' || normalized === 'US' || normalized === 'USA') return 'US'
  return 'ZA'
}

function stableNumber(value: number, decimals: number, decimalSeparator: string, thousandSeparator: string) {
  const fixed = value.toFixed(decimals)
  const [wholeRaw, decimalRaw = ''] = fixed.split('.')
  const whole = wholeRaw.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)
  return decimals > 0 ? `${whole}${decimalSeparator}${decimalRaw}` : whole
}

export function resolveLocalisationConfig(
  input?: Partial<{
    regionCode: string | null
    country: string | null
    locale: string | null
    currency: string | null
    taxLabel: string | null
    defaultTaxRate: number | null
    phoneCountryCode: string | null
  }>,
): LocalisationConfig {
  const resolvedRegion = toRegionCode(input?.regionCode) ?? countryToRegionCode(input?.country)
  const base = REGION_DEFAULTS[resolvedRegion]
  const locale = sanitizeLocale(input?.locale) ?? base.locale
  const currency = String(input?.currency ?? base.currency).trim().toUpperCase() || base.currency
  const taxLabel = String(input?.taxLabel ?? base.taxLabel).trim() || base.taxLabel
  const defaultTaxRate = Number.isFinite(input?.defaultTaxRate) ? Number(input?.defaultTaxRate) : base.defaultTaxRate
  const phoneCountryCode = String(input?.phoneCountryCode ?? base.phoneCountryCode).trim() || base.phoneCountryCode

  return {
    ...base,
    locale,
    currency,
    taxLabel,
    defaultTaxRate,
    phoneCountryCode,
  }
}

export function resolveLocalisationForBusiness(
  business: (Partial<BusinessProfile> & {
    region_code?: string | null
    locale_code?: string | null
    currency_code?: string | null
    tax_label?: string | null
    phone_country_code?: string | null
  }) | null | undefined,
) {
  return resolveLocalisationConfig({
    regionCode: business?.region_code ?? null,
    country: business?.country ?? null,
    locale: business?.locale_code ?? null,
    currency: business?.currency_code ?? null,
    taxLabel: business?.tax_label ?? null,
    defaultTaxRate: business?.default_vat_rate ?? null,
    phoneCountryCode: business?.phone_country_code ?? null,
  })
}

/**
 * En-dash for negatives (per design spec §10: "−R 1 500" with en-dash,
 * never hyphen). Render output inside a `<Money>` component or apply the
 * `.bb-money` utility class so tabular-nums + nowrap + weight 800 hold.
 */
const NEGATIVE_SIGN = '–'

export function formatCurrencyByConfig(
  amount: number,
  currency: string,
  config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG,
) {
  const safeAmount = Number.isFinite(amount) ? amount : 0
  const negative = safeAmount < 0
  const absolute = Math.abs(safeAmount)
  const resolvedCurrency = String(currency ?? config.currency).trim().toUpperCase() || config.currency
  const body = stableNumber(absolute, 2, config.decimalSeparator, config.thousandSeparator)
  const sign = negative ? NEGATIVE_SIGN : ''

  if (resolvedCurrency === config.currency) {
    if (config.currencySymbolPosition === 'suffix') {
      return `${sign}${body} ${config.currencySymbol}`
    }
    return `${sign}${config.currencySymbol} ${body}`
  }

  return `${sign}${resolvedCurrency} ${body}`
}

export function formatNumberByConfig(
  value: number,
  decimals = 2,
  config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG,
) {
  const safe = Number.isFinite(value) ? value : 0
  const negative = safe < 0
  const body = stableNumber(Math.abs(safe), Math.max(0, decimals), config.decimalSeparator, config.thousandSeparator)
  return `${negative ? NEGATIVE_SIGN : ''}${body}`
}

/**
 * Format a phone number into the regional canonical pattern. ZA → "+27 XX XXX XXXX",
 * GB → "+44 XXXX XXXXXX", US → "+1 (XXX) XXX-XXXX". Falls back to the raw input
 * if it can't be normalised so callers never get an empty string for bad data.
 */
export function formatPhoneByConfig(
  input: string | null | undefined,
  config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG,
) {
  const raw = String(input ?? '').trim()
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  if (!digits) return raw

  const countryDigits = config.phoneCountryCode.replace(/\D/g, '')
  const national = digits.startsWith(countryDigits)
    ? digits.slice(countryDigits.length).replace(/^0+/, '')
    : digits.replace(/^0+/, '')

  if (!national) return raw

  switch (config.regionCode) {
    case 'ZA': {
      // 9 national digits → 2-3-4
      if (national.length !== 9) return raw
      return `${config.phoneCountryCode} ${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}`
    }
    case 'GB': {
      if (national.length < 9 || national.length > 10) return raw
      return `${config.phoneCountryCode} ${national.slice(0, 4)} ${national.slice(4)}`
    }
    case 'US': {
      if (national.length !== 10) return raw
      return `${config.phoneCountryCode} (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
    }
    default:
      return `${config.phoneCountryCode} ${national}`
  }
}

export function formatDateByConfig(
  date: string | Date,
  config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG,
) {
  return new Intl.DateTimeFormat(config.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(date))
}

export function formatDateTimeByConfig(
  date: string | Date,
  config: LocalisationConfig = DEFAULT_LOCALISATION_CONFIG,
) {
  return new Intl.DateTimeFormat(config.locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}
